import React, { useRef, useEffect, useState, useCallback, memo } from 'react';
import useWindowSize from '../customHooks/useWindowSize';
import BearGridTooltip from './bearGridTooltip';
import randomMosiac from '../helpers/randomMosiacNew';
import StaticGrid from './staticGrid';
import Tooltip from './reusable/Tooltip';

// Lazy load the large mosaic images
const orderedMosiacSrc = new URL('../images/new-mosiac-ordered.png', import.meta.url).href;
const randomMosiacSrc = new URL('../images/new-mosiac.png', import.meta.url).href;

const Grid: React.FC = () => {

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  const [isRandom, setIsRandom] = useState<boolean>(true);
  const [scale, setScale] = useState(.5);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(true);
  const [imageLoadProgress, setImageLoadProgress] = useState<number>(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState({ x: 0, y: 0 });
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [selectedBear, setSelectedBear] = useState(1);
  const [tooltipCoords, setTooltipCoords] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isDragNMove, setIsDragNMove] = useState(false);


  const [animDisplayOne, setAnimDisplayOne] = useState(false);

  const size = useWindowSize();

  useEffect(() => {
    setTimeout(() => {
      setAnimDisplayOne(true);
    }, 200)
  }, [])

  // Lazy load image with progress tracking - deferred to allow UI to render first
  useEffect(() => {
    // Skip loading if not on desktop
    if (size[0] <= 1024) {
      setImageLoading(false);
      return;
    }

    setImageLoading(true);
    setImageLoadProgress(0);
    
    const imageSrc = isRandom ? randomMosiacSrc : orderedMosiacSrc;
    
    // Use requestIdleCallback or setTimeout to defer loading
    const loadImage = () => {
      // Try to load with progress using XMLHttpRequest
      const xhr = new XMLHttpRequest();
      xhr.open('GET', imageSrc, true);
      xhr.responseType = 'blob';
      
      xhr.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setImageLoadProgress(progress);
        }
      };
      
      xhr.onload = () => {
        if (xhr.status === 200) {
          const blob = xhr.response;
          const objectUrl = URL.createObjectURL(blob);
          const newImg = new Image();
          newImg.decoding = 'async';
          
          newImg.onload = () => {
            setImg(newImg);
            setImageLoading(false);
            setOffset({
              x: -1 * (Math.floor(Math.random() * (300 - 100 + 1)) + 100),
              y: -1 * (Math.floor(Math.random() * (4000 - 1000 + 1)) + 1000)
            });
            // Clean up object URL after image is loaded
            URL.revokeObjectURL(objectUrl);
          };
          
          newImg.src = objectUrl;
        }
      };
      
      xhr.onerror = () => {
        // Fallback to regular image loading
        const newImg = new Image();
        newImg.decoding = 'async';
        newImg.onload = () => {
          setImg(newImg);
          setImageLoading(false);
          setOffset({
            x: -1 * (Math.floor(Math.random() * (300 - 100 + 1)) + 100),
            y: -1 * (Math.floor(Math.random() * (4000 - 1000 + 1)) + 1000)
          });
        };
        newImg.src = imageSrc;
      };
      
      xhr.send();
    };

    // Defer image loading to next idle period or after a small delay
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(loadImage, { timeout: 100 });
    } else {
      setTimeout(loadImage, 50);
    }
  }, [isRandom, size])


  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove', handleTouchMove);
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [canvasRef.current]);

  useEffect(() => {

    const wrap = wrapperRef.current;
    if (!wrap) return;

    wrap.addEventListener("wheel", (event) => { event.preventDefault() }, { passive: false })

    return () => {
      wrap.removeEventListener("wheel", (event) => { event.preventDefault() })
    }

  }, [wrapperRef.current])


  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    if (!img) {
      return;
    }

    // Cancel any pending animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Use requestAnimationFrame for smoother rendering
    animationFrameRef.current = requestAnimationFrame(() => {
      if (scale < .65) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'medium';
      } else {
        ctx.imageSmoothingEnabled = false;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.translate(offset.x, offset.y);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      ctx.restore();
    });

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [scale, offset, img, size]);

  // Optimized click handler - calculate bear from coordinates instead of 6800+ event listeners
  const calculateBearFromCoords = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const canvasRect = canvas.getBoundingClientRect();
    const x = clientX - canvasRect.left;
    const y = clientY - canvasRect.top;

    // Convert screen coords to grid position
    const gridX = Math.floor((x - offset.x) / (175 * scale));
    const gridY = Math.floor((y - offset.y) / (175 * scale));

    // Check bounds (99 columns, 69 rows)
    if (gridX < 0 || gridX >= 99 || gridY < 0 || gridY >= 69) {
      return null;
    }

    // Calculate bear ID (1-indexed, row-major order)
    const bearIndex = gridY * 99 + gridX;
    const bearId = isRandom ? randomMosiac[bearIndex] : bearIndex + 1;

    return bearId;
  }, [offset, scale, isRandom]);

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    if (tooltipRef.current) {
      const { left, top, right, bottom } = tooltipRef.current.getBoundingClientRect();
      if (
        event.clientX < left ||
        event.clientX > right ||
        event.clientY < top ||
        event.clientY > bottom
      ) {
        setTooltipOpen(false);
      }
    }

    if (!dragging) {
      return;
    }

    setIsDragNMove(true);

    // @ts-ignore
    const maxOffsetX = (Number(img?.width || 0)) * scale - size[0] / 1.15;
    // @ts-ignore
    const maxOffsetY = (Number(img?.height || 0)) * scale - size[0] / 3;

    const newOffsetX = Math.min(Math.max(offset.x + (event.clientX - start.x) / scale, -maxOffsetX), 0);
    const newOffsetY = Math.min(Math.max(offset.y + (event.clientY - start.y) / scale, -maxOffsetY), 0);

    setOffset({ x: newOffsetX, y: newOffsetY });
    setStart({ x: event.clientX, y: event.clientY });
  };



  const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    setDragging(true);
    setStart({ x: event.clientX, y: event.clientY });
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    setDragging(false);
    if (isDragNMove === true) {
      setIsDragNMove(false);
    } else {
      // Calculate which bear was clicked using optimized method
      const bearId = calculateBearFromCoords(event.clientX, event.clientY);
      if (bearId) {
        setSelectedBear(bearId);
      }
      setTooltipOpen(true);
    }
    setTooltipCoords({ x: event.clientX, y: event.clientY });
  };

  const handleWheel = useCallback((event: React.WheelEvent<HTMLCanvasElement>) => {
    const delta = event.deltaY < 0 ? 1.1 : 1 / 1.1;
    const newScale = Math.min(Math.max(scale * delta, 0.2), 2); // Clamp scale
    setScale(newScale);
    setOffset({
      x: offset.x - (event.clientX - offset.x) * (delta - 1),
      y: offset.y - (event.clientY - offset.y) * (delta - 1),
    });
    setTooltipOpen(false);
  }, [scale, offset]);

  const handleTouchStart = (event: TouchEvent) => {
    setDragging(true);
    setStart({ x: event.touches[0].clientX, y: event.touches[0].clientY });
  };

  const handleTouchMove = (event: TouchEvent) => {
    if (!tooltipRef.current) return;
    const { left, top, right, bottom } = tooltipRef.current.getBoundingClientRect();
    if (
      event.touches[0].clientX < left ||
      event.touches[0].clientX > right ||
      event.touches[0].clientY < top ||
      event.touches[0].clientY > bottom
    ) {
      setTooltipOpen(false);
    }
    if (!dragging) {
      return;
    }
    setIsDragNMove(true);
    setOffset({
      x: offset.x + (event.touches[0].clientX / scale - start.x) / scale,
      y: offset.y + (event.touches[0].clientY / scale - start.y) / scale,
    });
    setStart({ x: event.touches[0].clientX / scale, y: event.touches[0].clientY / scale });
  };

  const handleTouchEnd = (event: TouchEvent) => {
    setDragging(false);
    if (isDragNMove) {
      setIsDragNMove(false);
    } else {
      setTooltipOpen(true);
    }
    setTooltipCoords({ x: offset.x + (event.touches[0].clientX / scale - start.x) / scale, y: offset.y + (event.touches[0].clientY / scale - start.y) / scale });
  };


  return (
    <div style={{ zIndex: 1 }} className={`flex justify-center transition-all duration-700 ease-out ${animDisplayOne ? '' : 'scale-0'}`}>

      <div>
        <div className="flex flex-row justify-between mb-3">
          <div className="flex flex-row ml-3">
            <p className="relative top-3 text-white">
              Select Bear Arrangement
            </p>
            <button
              type="button"
              className="mt-3 mr-3 flex align-items-center h-6 bg-center rounded border border-transparent bg-purple-500 px-3 text-base text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={() => { setIsRandom(!isRandom); }}
              onTouchStart={() => { setIsRandom(!isRandom); }}
            >
              {isRandom ? "RANDOM" : "ORDERED"}
            </button>
          </div>
          <span className='text-white relative top-3 mr-2'> {size[0] < 1024 && <Tooltip message='Click or touch to explore bears. View on larger screens for enhanced experience' style={{}} />} Click and drag to explore. Scroll to zoom.</span>
        </div>
        <div ref={wrapperRef} style={{ border: "1.25px solid #55bac3" }} className={`rounded-lg  transition-all duration-1000 ease-out ${animDisplayOne ? '' : 'scale-0'}`} >
          <div style={{ border: "1.25px solid #c4f6f9" }} className={`rounded-lg  transition-all duration-1000 ease-out ${animDisplayOne ? '' : 'scale-0'}`} >
            <div style={{ border: "1.25px solid #ffa2f8" }} className={`rounded-lg  transition-all duration-1000 ease-out ${animDisplayOne ? '' : 'scale-0'}`} >
              <div style={{ border: "1.25px solid #d733d2" }} className={`rounded-lg  transition-all duration-1000 ease-out ${animDisplayOne ? '' : 'scale-0'}`} >
                <div style={{ position: 'relative' }}>
                  {
                    size[0] > 1024 && (
                      <>
                        {/* Loading overlay */}
                        {imageLoading && (
                          <div 
                            className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800 rounded-lg z-10"
                            style={{ width: size[0] / 1.15, height: size[0] / 3 }}
                          >
                            <div className="text-[#c4f6f9] text-lg mb-3">Loading Mosaic...</div>
                            <div className="w-48 h-2 bg-gray-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-[#55bac3] to-[#ffa2f8] transition-all duration-300"
                                style={{ width: `${imageLoadProgress}%` }}
                              />
                            </div>
                            <div className="text-gray-400 text-sm mt-2">{imageLoadProgress}%</div>
                          </div>
                        )}
                        <canvas
                          ref={canvasRef}
                          className={`rounded-lg bg-gray-600 transition-all duration-1000 ease-out ${animDisplayOne ? '' : 'scale-0'} ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                          width={size[0] / 1.15}
                          height={size[0] / 3}
                          onMouseDown={handleMouseDown}
                          onMouseMove={handleMouseMove}
                          onMouseUp={handleMouseUp}
                          onWheel={handleWheel}
                          style={{
                            cursor: dragging ? 'grabbing' : 'pointer',
                            zIndex: 1,
                          }}
                        />
                      </>
                    )
                  }
                  {
                    size[0] <= 1024 &&
                    <StaticGrid animdisplay={animDisplayOne} width={size[0] / 1.15} height={size[0] / 3} isRandom={isRandom} />
                  }
                </div>
              </div>
            </div>
          </div>
        </div>


      </div>

      <BearGridTooltip tooltipRef={tooltipRef} bearId={selectedBear} coordinates={tooltipCoords} visible={tooltipOpen} />
    </div>
  );
};

export default memo(Grid);
